import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { State } from "../../../store";
import { BasketItem } from "../../../models/BasketItem";
import useFacility from "../../../../utils/hooks/useFacility";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import { GET_BASKET_URL } from "../../../api/apiRoutes";
import { canSendToGTM } from "../../../../utils/Analitics/analiticsConfigByFacility";
import {
  sendAddToCartEvent,
  sendRemoveFromCartEvent,
  convertBasketItemToGtmItem,
} from "../../../../utils/Analitics/GtmEvents";

/**
 * Globalny hook do automatycznego wykrywania zmian w koszyku i wysyłania eventów GTM
 * Reaguje na zmiany basketLength w Redux store i pobiera aktualną zawartość koszyka
 */
export default function useGlobalBasketGtmEvents() {
  const basketLength = useSelector((state: State) => state.basket.basketLength);
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const [previousBasket, setPreviousBasket] = useState<BasketItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (basketLength >= 0) {
      // Zawsze gdy basketLength się zmieni
      fetchBasketAndDetectChanges();
    }
    // eslint-disable-next-line
  }, [basketLength]);

  const fetchBasketAndDetectChanges = async () => {
    try {
      const { result, isError } = await get(GET_BASKET_URL);
      if (!isError && result?.basket) {
        const currentBasket = result.basket;

        if (isInitialized) {
          // Normalne wykrywanie zmian
          detectBasketChanges(previousBasket, currentBasket);
        } else {
          // Pierwsza inicjalizacja - tylko ustawiamy flagę, NIE wysyłamy add_to_cart
          setIsInitialized(true);
        }

        setPreviousBasket([...currentBasket]);
      }
    } catch (error) {
      console.error("Error fetching basket for GTM events:", error);
    }
  };

  // Funkcja wykrywająca zmiany w koszyku i wysyłająca eventy GTM
  const detectBasketChanges = (oldBasket: BasketItem[], newBasket: BasketItem[]) => {
    if (oldBasket.length === 0 && newBasket.length === 0) return;

    // Porównanie produktów między koszykami
    newBasket.forEach((newItem) => {
      const oldItem = oldBasket.find((item) => item.assortIds === newItem.assortIds);

      if (!oldItem) {
        // Nowy produkt dodany do koszyka

        if (canSendToGTM(facilityIds)) {
          sendAddToCartEvent(facilityIds, {
            currency: "PLN",
            value: newItem.price * newItem.qnt,
            items: [convertBasketItemToGtmItem(newItem)],
          });
        }
      } else if (oldItem.qnt < newItem.qnt) {
        // Zwiększenie ilości produktu

        const quantityDiff = newItem.qnt - oldItem.qnt;
        if (canSendToGTM(facilityIds)) {
          sendAddToCartEvent(facilityIds, {
            currency: "PLN",
            value: newItem.price * quantityDiff,
            items: [
              {
                ...convertBasketItemToGtmItem(newItem),
                quantity: quantityDiff,
              },
            ],
          });
        }
      }
    });

    // Sprawdzenie produktów usuniętych z koszyka
    oldBasket.forEach((oldItem) => {
      const newItem = newBasket.find((item) => item.assortIds === oldItem.assortIds);

      if (!newItem) {
        // Produkt usunięty z koszyka

        if (canSendToGTM(facilityIds)) {
          sendRemoveFromCartEvent(facilityIds, {
            currency: "PLN",
            value: oldItem.price * oldItem.qnt,
            items: [convertBasketItemToGtmItem(oldItem)],
          });
        }
      } else if (oldItem.qnt > newItem.qnt) {
        // Zmniejszenie ilości produktu

        const quantityDiff = oldItem.qnt - newItem.qnt;
        if (canSendToGTM(facilityIds)) {
          sendRemoveFromCartEvent(facilityIds, {
            currency: "PLN",
            value: oldItem.price * quantityDiff,
            items: [
              {
                ...convertBasketItemToGtmItem(oldItem),
                quantity: quantityDiff,
              },
            ],
          });
        }
      }
    });
  };
}
